package team.platforma.multiplatform_material_extension.ui.date

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import kotlinx.datetime.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format.DateTimeFormat
import kotlinx.datetime.format.FormatStringsInDatetimeFormats
import kotlinx.datetime.format.byUnicodePattern
import kotlinx.datetime.minus
import kotlinx.datetime.todayIn
import materialextension.multiplatform_material_extension.generated.resources.Res
import materialextension.multiplatform_material_extension.generated.resources.ic24_mel_calendar
import materialextension.multiplatform_material_extension.generated.resources.mel_ic_calendar
import materialextension.multiplatform_material_extension.generated.resources.mel_select_date_period
import org.jetbrains.compose.resources.StringResource
import team.platforma.multiplatform_material_extension.calendar.calendar_period.dialog.PeriodDatePiker
import team.platforma.multiplatform_material_extension.calendar.properties.DatePikerProperties
import team.platforma.multiplatform_material_extension.calendar.utils.CalendarRange
import team.platforma.multiplatform_material_extension.calendar.utils.setStandardCalendarRange
import team.platforma.multiplatform_material_extension.ui.core.InputField
import team.platforma.multiplatform_material_extension.ui.core.InputFieldProperties
import team.platforma.multiplatform_material_extension.ui.core.InputFieldStateListener
import team.platforma.multiplatform_material_extension.ui.core.StyledTextField
import team.platforma.multiplatform_material_extension.ui.core.field_utils.endIcon
import team.platforma.multiplatform_material_extension.ui.core.global_style.area.supportArea
import team.platforma.multiplatform_material_extension.ui.core.global_style.setFiledTextFieldDefaults
import team.platforma.multiplatform_material_extension.ui.core.global_style.setOutlinedTextFieldDefaults
import team.platforma.multiplatform_material_extension.ui.core.global_style.shape.setFilledTextFieldDefaultShape
import team.platforma.multiplatform_material_extension.ui.core.global_style.shape.setOutlineTextFieldDefaultShape
import team.platforma.multiplatform_material_extension.ui.core.rememberInputFieldState
import team.platforma.multiplatform_material_extension.util.forms.FormProperties
import team.platforma.multiplatform_material_extension.util.forms.keyListener
import team.platforma.multiplatform_material_extension.util.forms.rememberForms
import team.platforma.multiplatform_material_extension.util.visual_transformation.MaskVisualTransformation


/**
 * Инициализация поля ввода периода дат
 * @author Evgeny Minaenkov
 * @see [DateFieldPeriod]
 * @sample dateFieldPeriodInitializeSample
 *
 */
@Deprecated("")
object DateFieldPeriod : InputField {
    @OptIn(FormatStringsInDatetimeFormats::class)
    @Composable
    fun create(
        modifier: Modifier? = null,
        startDate: LocalDate? = null,
        endDate: LocalDate? = null,
        onPeriodDateChange: (startDate: LocalDate?, endDate: LocalDate?) -> Unit,
        datePikerTitle: StringResource = Res.string.mel_select_date_period,
        localDateFormat: DateTimeFormat<LocalDate> = LocalDate.Format { byUnicodePattern("dd.MM.yyyy") },
        mask: String = "##.##.#### — ##.##.####",
        maskPart: String = "##.##.####",
        maskChar: Char = '#',
        required: Boolean = true,
        dateInputMessage: DateInputMessage? = null,
        calendarRange: CalendarRange? = null,
        formProperties: FormProperties? = null
    ): StyledTextField {

        val form by rememberForms()

        val mForm = formProperties ?: FormProperties(form, key = "")

        val currentDate = Clock.System.todayIn(TimeZone.currentSystemDefault())
        val mCalendarRange = calendarRange ?: setStandardCalendarRange(currentDate)


        LaunchedEffect(mForm.key) {
            val initializeDateString = "${startDate.formatToInputString(localDateFormat)}${
                endDate.formatToInputString(localDateFormat)
            }"

            mForm.form.initValue(mForm.key, initializeDateString)
            mForm.form.changeValue(mForm.key, initializeDateString)

        }

        return builder(
            DateFieldPeriodImpl(
                areaModifier = modifier,
                startDate = startDate,
                endDate = endDate,
                localDateFormat = localDateFormat,
                mask = mask,
                maskChar = maskChar,
                required = required,
                dateInputMessage = dateInputMessage,
                calendarRange = mCalendarRange,
                form = mForm,
                datePikerTitle = datePikerTitle,
                maskPart = maskPart,
                onPeriodDateChange = { firstDate, secondDate ->
                    onPeriodDateChange(firstDate, secondDate)
                }
            )
        )
    }

    override fun builder(textFieldImpl: StyledTextField): StyledTextField = textFieldImpl

}


@Composable
private fun DateFieldPeriodImpl(
    areaModifier: Modifier?,
    startDate: LocalDate?,
    endDate: LocalDate?,
    onPeriodDateChange: (firstDate: LocalDate?, secondDate: LocalDate?) -> Unit,
    localDateFormat: DateTimeFormat<LocalDate>,
    datePikerTitle: StringResource,
    mask: String,
    maskPart: String,
    maskChar: Char,
    required: Boolean,
    dateInputMessage: DateInputMessage?,
    calendarRange: CalendarRange,
    form: FormProperties
): StyledTextField {

    val inputFieldState = rememberInputFieldState()

    val focusRequester = remember { FocusRequester() }
    val index = remember { form.form.registerFocus(focusRequester) }

    var showDatePiker by remember { mutableStateOf(false) }

    var stringPeriod by remember {
        mutableStateOf(
            "${startDate.formatToInputString(localDateFormat)}${
                endDate.formatToInputString(
                    localDateFormat
                )
            }"
        )
    }

    val visualTransformation = remember { MaskVisualTransformation(mask, maskChar) }
    val visualTransformationPart = remember { MaskVisualTransformation(maskPart, maskChar) }

    var error by remember { mutableStateOf(false) }

    var showMessage by remember { mutableStateOf(false) }

    val mDateInputMessage = dateInputMessage ?: DateInputMessage()

    var message by remember { mutableStateOf(mDateInputMessage.help) }


    LaunchedEffect(Unit) {
        if (required) {
            if (startDate != null && endDate != null) {
                if (startDate in calendarRange.max..calendarRange.min &&
                    endDate in calendarRange.max..calendarRange.min
                ) {
                    form.form.setValidateState(form.key, true)
                } else {
                    // одна из дат вне допустимых значений
                    form.form.setValidateState(form.key, false)
                }
            } else {
                form.form.setValidateState(form.key, false)
            }
        } else {
            form.form.setValidateState(form.key, startDate == null && endDate == null)
        }
    }


    DisposableEffect(inputFieldState) {

        val listener = object : InputFieldStateListener {

            override fun onEndIconClick() {
                showDatePiker = !showDatePiker
            }

            override fun onValueChange(string: String) {
                val maskLength = mask.count { it == maskChar }
                stringPeriod = string.trim().take(maskLength)

                if (stringPeriod.length == maskLength) {
                    val middle = maskLength / 2
                    val firstStringDate = stringPeriod.substring(0, middle)
                    val secondStringDate = stringPeriod.substring(middle)


                    val firstResultStringDate =
                        visualTransformationPart.filter(AnnotatedString(firstStringDate)).text.text
                    val secondResultStringDate =
                        visualTransformationPart.filter(AnnotatedString(secondStringDate)).text.text

                    try {
                        val firstDate = firstResultStringDate.toLocalDate(localDateFormat)
                        val secondDate = secondResultStringDate.toLocalDate(localDateFormat)

                        var firstResultDate = firstDate
                        var secondResultDate = secondDate

                        if (secondDate < firstDate) {
                            secondResultDate = firstDate
                            firstResultDate = secondDate
                            stringPeriod =
                                "${firstResultDate.formatToInputString(localDateFormat)}${
                                    secondResultDate.formatToInputString(localDateFormat)
                                }"
                        }

                        if (firstDate in calendarRange.max..calendarRange.min &&
                            secondDate in calendarRange.max..calendarRange.min
                        ) {
                            error = false
                            form.form.setValidateState(form.key, true)
                            onPeriodDateChange(
                                firstResultDate,
                                secondResultDate
                            )
                        } else {
                            // одна из дат вне допустимых значений
                            error = true
                            form.form.setValidateState(form.key, false)
                            onPeriodDateChange(
                                null,
                                null
                            )
                        }

                    } catch (exception: Exception) {
                        error = true
                        form.form.setValidateState(form.key, false)
                        onPeriodDateChange(
                            null,
                            null
                        )
                    }

                } else {
                    if (required) {
                        if (stringPeriod.isEmpty()) {
                            // поле обязательное
                            error = true
                            form.form.setValidateState(form.key, false)
                        } else {
                            if (stringPeriod.length < maskLength) {
                                error = false
                                form.form.setValidateState(form.key, false)
                            }
                        }
                    } else {
                        if (stringPeriod.isEmpty()) {
                            // поле не обязательное
                            form.form.setValidateState(form.key, true)
                        } else {
                            if (stringPeriod.length < maskLength) {
                                form.form.setValidateState(form.key, false)
                            }
                        }
                    }
                    onPeriodDateChange(
                        null,
                        null
                    )
                }

                form.form.changeValue(form.key, stringPeriod)
            }

        }

        inputFieldState.addListener(listener)

        onDispose {
            inputFieldState.removeListener(listener)
        }
    }

    if (showDatePiker) {
        PeriodDatePiker(
            onDismiss = { showDatePiker = !showDatePiker },
            startDate = startDate,
            endDate = endDate,
            onPeriodSelected = { firstDate, secondDate ->
                stringPeriod = "${firstDate.formatToInputString(localDateFormat)}${
                    secondDate.formatToInputString(localDateFormat)
                }"
                onPeriodDateChange(firstDate, secondDate)
                form.form.changeValue(form.key, stringPeriod)
                form.form.setValidateState(form.key, true)
                error = false
            },
            properties = DatePikerProperties(
                animateUserScroll = true,
                animateClickChangeButtonMonth = true,
                dismissOnBackPress = true,
                dismissOnClickOutside = true
            ),
            calendarRange = calendarRange,
            title = datePikerTitle
        )
    }


    val style = object : StyledTextField {
        @Composable
        override fun Outline(properties: InputFieldProperties?) {
            val mProperties = properties ?: InputFieldProperties()

            Column(
                modifier = (areaModifier ?: Modifier).width(IntrinsicSize.Min)
                    .heightIn(min = supportArea())
            ) {
                OutlinedTextField(
                    value = stringPeriod,
                    onValueChange = { change ->
                        inputFieldState.changeValue(change)
                    },
                    label = mProperties.label,
                    placeholder = mProperties.placeholder,
                    modifier = (mProperties.modifier ?: Modifier)
                        .keyListener(Key.Tab) {
                            form.form.moveToNextFocus(index)
                        }.focusRequester(focusRequester),
                    trailingIcon = {
                        endIcon(
                            drawableResource = mProperties.endIcon
                                ?: Res.drawable.ic24_mel_calendar,
                            inputFieldState = inputFieldState
                        )
                    },
                    visualTransformation = visualTransformation,
                    isError = error,
                    keyboardOptions = mProperties.keyboardOptions
                        ?: KeyboardOptions(keyboardType = KeyboardType.Number),
                    colors = properties?.colors ?: setOutlinedTextFieldDefaults(),
                    shape = properties?.shape ?: setOutlineTextFieldDefaultShape(),
                )
                Column(
                    modifier = Modifier.heightIn(min = supportArea()).fillMaxWidth()
                ) {
                    AnimatedVisibility(showMessage) {
                        val helperTextColors = properties?.colors ?: setOutlinedTextFieldDefaults()
                        Text(
                            modifier = Modifier.padding(horizontal = 16.dp),
                            text = message,
                            color = if (error) helperTextColors.errorSupportingTextColor else helperTextColors.focusedSupportingTextColor,
                            style = MaterialTheme.typography.bodySmall
                        )
                    }
                }
            }
        }


        @Composable
        override fun Filled(properties: InputFieldProperties?) {
            val mProperties = properties ?: InputFieldProperties()

            Column(
                modifier = (areaModifier ?: Modifier).width(IntrinsicSize.Min)
            ) {
                TextField(
                    value = stringPeriod,
                    onValueChange = { change ->
                        inputFieldState.changeValue(change)
                    },
                    label = mProperties.label,
                    placeholder = mProperties.placeholder,
                    modifier = (mProperties.modifier ?: Modifier)
                        .keyListener(Key.Tab) {
                            form.form.moveToNextFocus(index)
                        }.focusRequester(focusRequester),
                    trailingIcon = {
                        endIcon(
                            drawableResource = mProperties.endIcon
                                ?: Res.drawable.ic24_mel_calendar,
                            inputFieldState = inputFieldState
                        )
                    },
                    visualTransformation = visualTransformation,
                    isError = error,
                    keyboardOptions = mProperties.keyboardOptions
                        ?: KeyboardOptions(keyboardType = KeyboardType.Number),
                    colors = properties?.colors ?: setFiledTextFieldDefaults(),
                    shape = properties?.shape ?: setFilledTextFieldDefaultShape(),
                )
                Column(
                    modifier = Modifier.heightIn(min = supportArea()).fillMaxWidth()
                ) {
                    AnimatedVisibility(showMessage) {
                        val helperTextColors = properties?.colors ?: setFiledTextFieldDefaults()
                        Text(
                            modifier = Modifier.padding(horizontal = 16.dp),
                            text = message,
                            color = if (error) helperTextColors.errorSupportingTextColor else helperTextColors.focusedSupportingTextColor,
                            style = MaterialTheme.typography.bodySmall
                        )
                    }
                }
            }
        }

    }
    return style
}


private fun LocalDate?.formatToInputString(dateTimeFormat: DateTimeFormat<LocalDate>): String {
    return this?.let { date ->
        dateTimeFormat.format(date).replace(Regex("[^\\w\\s]"), "")
    } ?: ""
}

private fun LocalDate?.to(dateTimeFormat: DateTimeFormat<LocalDate>): String {
    return try {
        this?.let { date ->
            dateTimeFormat.format(date)
        } ?: ""
    } catch (e: Exception) {
        ""
    }
}

private fun String.toLocalDate(dateTimeFormat: DateTimeFormat<LocalDate>): LocalDate {
    return dateTimeFormat.parse(this)
}



@Composable
internal fun dateFieldPeriodInitializeSample() {
    val currentDate = Clock.System.todayIn(TimeZone.currentSystemDefault())
    val calendarRange = CalendarRange(
        min = currentDate.minus(DatePeriod(years = 18)),
        max = currentDate.minus(DatePeriod(years = 101))
    )


    val form by rememberForms()

    DateFieldPeriod.create(
        startDate = currentDate,
        endDate = currentDate,
        calendarRange = calendarRange,
        formProperties = FormProperties(
            form = form,
            key = "simple_key"
        ),
        onPeriodDateChange = { firstDate, secondDate ->

        }
    ).Outline( // or Filled
        properties = InputFieldProperties().simpleLabel("Input field label")
    )
}

